import React, { useEffect, useMemo, useState } from "react";
import { m } from "framer-motion";
import {
  Check, Languages, LockKeyhole, Mic, NotebookPen, RefreshCw,
  Sparkles, Square, Volume2,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { VOICE_AIRA_SCENARIOS, voiceAiraSceneSources } from "./voiceAiraScenarios";

const EASE = [0.22, 1, 0.36, 1];
const REFINEMENT_TAGS = ["fillers removed", "grammar fixed", "meaning kept"];
const DEFAULT_SCENARIO = VOICE_AIRA_SCENARIOS[0];
const DEFAULT_SOURCES = voiceAiraSceneSources(DEFAULT_SCENARIO.imageStem);

function variant(reduce, hidden, show) {
  return reduce ? undefined : { hidden, show };
}

function RecordingBars({ reduce, dark = true }) {
  const heights = [9, 17, 12, 22, 15, 25, 11, 19, 13, 23, 10, 16];
  return (
    <span className="flex h-7 items-center gap-[3px]" aria-hidden="true">
      {heights.map((height, index) => (
        <m.span
          key={`${height}-${index}`}
          className={cn("block w-[3px] rounded-full", dark ? "bg-white/85" : "bg-ai/70")}
          style={{ height }}
          variants={variant(
            reduce,
            { transform: "scaleY(0.35)", opacity: 0.5 },
            {
              transform: ["scaleY(0.35)", "scaleY(1)", "scaleY(0.55)", "scaleY(0.82)"],
              opacity: [0.5, 1, 0.72, 0.9],
              transition: { duration: 1.35, delay: 0.45 + index * 0.055, ease: "easeInOut" },
            },
          )}
        />
      ))}
    </span>
  );
}

function DeviceVoicePreview({ scenario }) {
  const [speaking, setSpeaking] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    setSpeaking(false);
    setMessage("");
    if (typeof window !== "undefined" && window.speechSynthesis) window.speechSynthesis.cancel();
    return () => {
      if (typeof window !== "undefined" && window.speechSynthesis) window.speechSynthesis.cancel();
    };
  }, [scenario.id]);

  const toggle = () => {
    if (typeof window === "undefined" || !window.speechSynthesis || !window.SpeechSynthesisUtterance) {
      setMessage("This browser does not offer an installed voice preview.");
      return;
    }
    const synth = window.speechSynthesis;
    if (speaking) {
      synth.cancel();
      setSpeaking(false);
      setMessage("Voice preview stopped.");
      return;
    }

    const voices = typeof synth.getVoices === "function" ? synth.getVoices() : [];
    const wanted = scenario.browserLocale.toLowerCase();
    const base = wanted.split("-")[0];
    const voice = voices.find((item) => item.lang?.toLowerCase() === wanted)
      || voices.find((item) => item.lang?.toLowerCase().split("-")[0] === base);
    if (voices.length > 0 && !voice) {
      setMessage(`No ${scenario.language} system voice is installed on this device.`);
      return;
    }

    const utterance = new window.SpeechSynthesisUtterance(scenario.spokenText);
    utterance.lang = scenario.browserLocale;
    utterance.rate = 0.92;
    if (voice) utterance.voice = voice;
    utterance.onend = () => {
      setSpeaking(false);
      setMessage(`${scenario.language} device-voice preview finished.`);
    };
    utterance.onerror = () => {
      setSpeaking(false);
      setMessage(`The ${scenario.language} device voice could not play.`);
    };
    synth.cancel();
    synth.speak(utterance);
    setSpeaking(true);
    setMessage(`Playing an opt-in ${scenario.language} device-voice preview.`);
  };

  return (
    <div className="flex min-w-0 flex-wrap items-center justify-end gap-x-2 gap-y-1">
      <button
        type="button"
        data-testid="voice-device-preview"
        onClick={toggle}
        className="inline-flex min-h-11 items-center gap-2 rounded-full border border-ai/15 bg-ai/[0.06] px-3.5 text-[11px] font-bold text-ai-ink transition-[background-color,border-color,transform] hover:border-ai/30 hover:bg-ai/[0.1] active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-ai/45"
        aria-label={`${speaking ? "Stop" : "Hear"} the ${scenario.language} phrase with this device's installed voice`}
      >
        {speaking ? <Square className="h-3.5 w-3.5" fill="currentColor" aria-hidden="true" /> : <Volume2 className="h-4 w-4" aria-hidden="true" />}
        {speaking ? "Stop voice" : `Hear ${scenario.language}`}
      </button>
      <span className="max-w-[30rem] text-right text-[10px] leading-relaxed text-ink-subtle" aria-live="polite">
        {message || "Opt-in device voice · no audio is uploaded or stored"}
      </span>
    </div>
  );
}

function ScenarioTabs({ activeId, onChange }) {
  return (
    <div className="mb-3">
      <div
        className="grid grid-cols-2 gap-1.5 rounded-[18px] border border-[#d9d0c6] bg-[#eee7de]/90 p-1.5 shadow-[0_12px_32px_-28px_rgba(39,25,17,0.7)] backdrop-blur-md dark:border-white/10 dark:bg-white/[0.06] sm:grid-cols-4"
        role="tablist"
        aria-label="Voice Aira example conversations"
      >
        {VOICE_AIRA_SCENARIOS.map((item) => {
          const selected = item.id === activeId;
          return (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={selected}
              aria-controls="voice-aira-active-story"
              id={`voice-aira-tab-${item.id}`}
              onClick={() => onChange(item.id)}
              className={cn(
                "min-h-12 rounded-[13px] px-3 py-2 text-left transition-[background-color,color,box-shadow,transform] duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-ai/50",
                selected
                  ? "bg-[#17111f] text-white shadow-[0_10px_28px_-18px_rgba(23,17,31,0.9)]"
                  : "text-[#5f554b] hover:bg-white/70 hover:text-[#211a16] dark:text-white/65 dark:hover:bg-white/[0.08] dark:hover:text-white",
              )}
            >
              <span className="block text-[10px] font-bold uppercase tracking-[0.12em] opacity-65">{item.nativeLanguage} · {item.language}</span>
              <span className="mt-0.5 block text-[12px] font-bold">{item.tab}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * An illustrated, finite product story shared by the homepage and /modules/voice-aira. The
 * generated raster supplies only fictional school context. Recording state, transcripts, cleanup,
 * notes and controls remain real DOM, while sound is opt-in and uses only a device-installed voice.
 */
export default function VoiceAiraProductFilm({
  reduce = false,
  className,
  testId = "voice-aira-product-film",
  showReplay = true,
  priority = false,
}) {
  const [activeId, setActiveId] = useState(DEFAULT_SCENARIO.id);
  const [run, setRun] = useState(0);
  const scenario = useMemo(
    () => VOICE_AIRA_SCENARIOS.find((item) => item.id === activeId) || DEFAULT_SCENARIO,
    [activeId],
  );
  const sources = useMemo(() => voiceAiraSceneSources(scenario.imageStem), [scenario.imageStem]);

  const changeScenario = (nextId) => {
    setActiveId(nextId);
    setRun((value) => value + 1);
  };

  return (
    <figure className={cn("relative", className)} data-testid={testId}>
      <ScenarioTabs activeId={scenario.id} onChange={changeScenario} />

      <m.div
        key={`${scenario.id}-${run}`}
        id="voice-aira-active-story"
        role="tabpanel"
        aria-labelledby={`voice-aira-tab-${scenario.id}`}
        className="relative isolate overflow-hidden rounded-[30px] border border-[#30263b] bg-[#17111f] shadow-[0_34px_100px_-46px_rgba(44,24,66,0.72)]"
        data-motion="scripted-finite"
        initial={reduce ? false : "hidden"}
        animate={reduce ? undefined : "show"}
      >
        <div className="pointer-events-none absolute inset-0 opacity-50 [background-image:radial-gradient(circle_at_12%_12%,rgba(124,58,237,0.24),transparent_32%),radial-gradient(circle_at_88%_82%,rgba(29,78,216,0.18),transparent_34%)]" aria-hidden="true" />
        <div className="pointer-events-none absolute inset-0 opacity-[0.12] [background-image:linear-gradient(rgba(255,255,255,0.16)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.16)_1px,transparent_1px)] [background-size:54px_54px] [mask-image:linear-gradient(to_right,black,transparent_72%)]" aria-hidden="true" />

        <div className="relative grid min-h-[790px] lg:min-h-[590px] lg:grid-cols-[minmax(0,0.88fr)_minmax(430px,1.12fr)]">
          <div className="relative min-h-[300px] overflow-hidden border-b border-white/10 lg:min-h-0 lg:border-b-0 lg:border-r">
            <m.div
              className="absolute inset-0"
              initial={reduce ? false : { transform: "scale(1.005)" }}
              animate={reduce ? undefined : { transform: "scale(1.045)" }}
              transition={{ duration: reduce ? 0 : 11.5, ease: "linear" }}
            >
              <picture className="block h-full w-full">
                <source type="image/avif" srcSet={sources.avif} sizes="(min-width: 1024px) 44vw, 100vw" />
                <source type="image/webp" srcSet={sources.webp} sizes="(min-width: 1024px) 44vw, 100vw" />
                <img
                  src={sources.src}
                  alt={scenario.imageAlt}
                  width="622"
                  height="622"
                  loading={priority ? "eager" : "lazy"}
                  fetchpriority={priority ? "high" : "auto"}
                  decoding="async"
                  className="h-full w-full object-cover"
                />
              </picture>
            </m.div>
            <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(15,10,20,0.06)_0%,rgba(15,10,20,0.03)_42%,rgba(15,10,20,0.78)_100%)]" aria-hidden="true" />

            <m.div
              className="absolute left-4 top-4 flex max-w-[calc(100%-2rem)] items-center gap-2 rounded-full border border-white/30 bg-[#17110d]/70 px-3 py-2 text-[10px] font-semibold text-white shadow-lg backdrop-blur-md sm:left-5 sm:top-5 sm:text-[11px]"
              variants={variant(reduce, { opacity: 0, transform: "translateY(-8px)" }, { opacity: 1, transform: "translateY(0px)", transition: { duration: 0.35, ease: EASE, delay: 0.15 } })}
            >
              <span className="relative grid h-7 w-7 shrink-0 place-items-center rounded-full bg-ai">
                <m.span
                  className="h-2 w-2 rounded-full bg-white"
                  variants={variant(reduce, { transform: "scale(1)" }, { transform: ["scale(1)", "scale(.7)", "scale(1.15)", "scale(1)"], transition: { duration: 2.1, ease: "easeInOut", delay: 0.35 } })}
                />
              </span>
              <span>Recording with consent</span>
              <span className="tabular-nums text-white/65">{scenario.duration}</span>
            </m.div>

            <m.div
              className="absolute inset-x-4 bottom-4 rounded-[18px] border border-white/20 bg-[#17111f]/78 p-3 text-white shadow-[0_20px_45px_-25px_rgba(0,0,0,0.8)] backdrop-blur-xl sm:inset-x-5 sm:bottom-5 sm:p-4"
              variants={variant(reduce, { opacity: 0, transform: "translateY(12px)" }, { opacity: 1, transform: "translateY(0px)", transition: { duration: 0.42, ease: EASE, delay: 0.5 } })}
            >
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <p className="truncate text-[10px] font-bold uppercase tracking-[0.14em] text-white/55">{scenario.title}</p>
                  <p className="mt-1 text-[13px] font-semibold text-white">{scenario.speaker}</p>
                </div>
                <RecordingBars reduce={reduce} />
              </div>
              <p className="mt-2 text-[10px] leading-relaxed text-white/55">Illustrated scenario · the product does not infer speaker identity in this example</p>
            </m.div>
          </div>

          <section className="relative bg-[#fbf9fc] p-4 text-left sm:p-6 lg:p-7 xl:p-8">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#e8e0ef] pb-4">
              <div className="flex items-center gap-2.5">
                <span className="grid h-10 w-10 place-items-center rounded-[13px] bg-ai text-white shadow-[0_10px_25px_-12px_rgba(124,58,237,0.8)]">
                  <Mic className="h-4.5 w-4.5" strokeWidth={2.2} aria-hidden="true" />
                </span>
                <div>
                  <p className="text-[12px] font-bold text-[#241b31]">Aira is taking notes</p>
                  <p className="text-[10px] text-[#665c70]">Live draft · nothing shared</p>
                </div>
              </div>
              <m.span
                className="inline-flex items-center gap-1.5 rounded-full border border-ai/20 bg-ai/[0.07] px-2.5 py-1 text-[10px] font-semibold text-[#5b21b6]"
                variants={variant(reduce, { opacity: 0, transform: "translateY(6px) scale(.96)" }, { opacity: 1, transform: "translateY(0px) scale(1)", transition: { duration: 0.35, ease: EASE, delay: 1.05 } })}
              >
                <Languages className="h-3 w-3" aria-hidden="true" /> {scenario.languageChip}
              </m.span>
            </div>

            <div className="mt-4 min-h-[112px] rounded-[20px] border border-[#ece6f1] bg-[#f4f0f7] px-4 py-3.5 sm:px-5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-[9px] font-bold uppercase tracking-[0.14em] text-[#655b6e]">From speech to clear intent</p>
                <p className="text-[9px] font-semibold text-[#655b6e]">{scenario.transcriptLabel}</p>
              </div>
              <div className="mt-2 grid">
                {!reduce ? (
                  <m.p
                    lang={scenario.htmlLang}
                    className="col-start-1 row-start-1 text-[12.5px] leading-[1.65] text-[#51485c] sm:text-[13.5px]"
                    variants={{
                      hidden: { opacity: 0, clipPath: "inset(0 100% 0 0)" },
                      show: { opacity: [0, 1, 1, 0], clipPath: ["inset(0 100% 0 0)", "inset(0 0% 0 0)", "inset(0 0% 0 0)", "inset(0 0% 0 0)"], transition: { duration: 2.55, times: [0, 0.38, 0.76, 1], ease: EASE, delay: 1.45 } },
                    }}
                  >
                    {scenario.rawParts.map((part, index) => (
                      <span key={`${part.text}-${index}`} className={part.filler ? "text-[#776c80] line-through decoration-ai/60" : undefined}>{part.text}</span>
                    ))}
                  </m.p>
                ) : null}
                <m.p
                  className="col-start-1 row-start-1 flex items-start gap-2 text-[12.5px] font-semibold leading-[1.65] text-[#241b31] sm:text-[13.5px]"
                  variants={variant(reduce, { opacity: 0, transform: "translateY(7px)" }, { opacity: 1, transform: "translateY(0px)", transition: { duration: 0.42, ease: EASE, delay: 4.05 } })}
                >
                  <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ai" aria-hidden="true" />
                  <span>{scenario.polished}</span>
                </m.p>
              </div>
              <m.div
                className="mt-2.5 flex flex-wrap gap-1.5 pl-5"
                variants={variant(reduce, { opacity: 0 }, { opacity: 1, transition: { duration: 0.35, delay: 4.45 } })}
              >
                {REFINEMENT_TAGS.map((tag) => (
                  <span key={tag} className="rounded-full bg-ai/[0.08] px-2 py-0.5 text-[9px] font-semibold text-[#5b21b6]">{tag}</span>
                ))}
              </m.div>
            </div>

            <m.div
              className="mt-3 rounded-[20px] border border-[#e7dfec] bg-[#ffffff] p-4 shadow-[0_16px_36px_-30px_rgba(49,25,79,0.65)] sm:p-5"
              variants={variant(reduce, { opacity: 0, transform: "translateY(12px)" }, { opacity: 1, transform: "translateY(0px)", transition: { duration: 0.45, ease: EASE, delay: 4.95 } })}
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.13em] text-[#5b21b6]">
                  <NotebookPen className="h-3.5 w-3.5" aria-hidden="true" /> Meeting note
                </p>
                <span className="rounded-full bg-[#eef4ff] px-2.5 py-1 text-[9px] font-semibold text-[#1d4ed8]">{scenario.linkLabel}</span>
              </div>
              <p className="mt-2.5 text-[11.5px] leading-[1.65] text-[#51485c] sm:text-[12px]">{scenario.note}</p>
              <ul className="mt-3 space-y-2">
                {scenario.actions.map((action, index) => (
                  <m.li
                    key={action}
                    className="flex items-start gap-2 text-[10.5px] leading-snug text-[#51485c] sm:text-[11px]"
                    variants={variant(reduce, { opacity: 0, transform: "translateY(5px)" }, { opacity: 1, transform: "translateY(0px)", transition: { duration: 0.28, ease: EASE, delay: 5.42 + index * 0.42 } })}
                  >
                    <span className="mt-px grid h-4 w-4 shrink-0 place-items-center rounded-[5px] border border-ai/30 bg-ai/[0.08]">
                      <m.span variants={variant(reduce, { opacity: 0, scale: 0 }, { opacity: 1, scale: 1, transition: { type: "spring", stiffness: 430, damping: 17, delay: 5.66 + index * 0.42 } })}>
                        <Check className="h-3 w-3 text-ai" strokeWidth={3} aria-hidden="true" />
                      </m.span>
                    </span>
                    {action}
                  </m.li>
                ))}
              </ul>
              <m.div
                className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-[#eee8f1] pt-3"
                variants={variant(reduce, { opacity: 0, transform: "translateY(5px)" }, { opacity: 1, transform: "translateY(0px)", transition: { duration: 0.4, ease: EASE, delay: 6.75 } })}
              >
                <span className="inline-flex items-center gap-1.5 rounded-full border border-[#bfdbfe] bg-[#eff6ff] px-2.5 py-1 text-[9.5px] font-bold text-[#1d4ed8]">
                  <Check className="h-3 w-3" strokeWidth={3} aria-hidden="true" /> Drafted for review
                </span>
                <span className="inline-flex items-center gap-1.5 text-[9.5px] font-semibold text-[#756a7e]">
                  <LockKeyhole className="h-3 w-3" aria-hidden="true" /> Transcript stays private
                </span>
              </m.div>
            </m.div>
          </section>
        </div>
      </m.div>

      <figcaption className="mt-3 grid gap-3 px-1 text-[11px] leading-relaxed text-ink-subtle lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
        <span>
          Illustrative scenario · synthetic sample data. Switch examples to see Hindi, Telugu, Odia and English conversations become reviewable drafts.
        </span>
        <div className="flex flex-wrap items-center justify-end gap-1.5">
          <DeviceVoicePreview scenario={scenario} />
          {showReplay && !reduce ? (
            <button
              type="button"
              onClick={() => setRun((value) => value + 1)}
              className="inline-flex min-h-11 items-center gap-2 rounded-full px-3 font-semibold text-ink-2 transition-colors hover:bg-ai/[0.06] hover:text-ai-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-ai/45"
              aria-label="Replay the Voice Aira product story"
            >
              <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" /> Replay
            </button>
          ) : null}
        </div>
      </figcaption>
    </figure>
  );
}

const HEARD_PARTS = DEFAULT_SCENARIO.rawParts;
const POLISHED_LINE = DEFAULT_SCENARIO.polished;
const ACTIONS = DEFAULT_SCENARIO.actions;
const SCENE_IMAGE = DEFAULT_SOURCES.src;
const SCENE_WEBP_SRCSET = DEFAULT_SOURCES.webp;
const SCENE_AVIF_SRCSET = DEFAULT_SOURCES.avif;

export {
  ACTIONS, HEARD_PARTS, POLISHED_LINE, REFINEMENT_TAGS,
  SCENE_AVIF_SRCSET, SCENE_IMAGE, SCENE_WEBP_SRCSET,
};
